import PropTypes from 'prop-types';

import { trans } from '@spotahome/soyuz/client';

import { formatPrice } from '@spotahome/soyuz-currency';

import {
  APPLY_ON_INCLUDED,
  COST_CATEGORY_BILLS,
  COST_CATEGORY_MONTHLY_RENT,
  PAYMENT_TO_SPOTAHOME
} from '../../constants/costs';

import { ListingCostsShape } from '../../constants/shapes';

import { formatMonthlyPaymentDate } from '../utils/dateUtils';

import { getUnit, isEstimatedCost } from './costHelpers';

import {
  formatCostTitle,
  getBillValue,
  getBillValueSubtitle,
  getTooltipText
} from './duringStayUtils';

import DetailRow from './DetailRow';
import PaymentDetailsCard from './PaymentDetailsCard';

import styles from './PaymentDetailsCard.module.scss';

const sameMonthlyRentValue = (a, b) =>
  a.value === b.value && a.valueWithoutOffer === b.valueWithoutOffer;

const groupConsecutiveMonthlyRentBySameValue = costs => {
  if (costs.length === 0) {
    return [];
  }
  const groups = [];
  let chunk = [costs[0]];
  for (let i = 1; i < costs.length; i += 1) {
    const curr = costs[i];
    if (sameMonthlyRentValue(chunk[chunk.length - 1], curr)) {
      chunk.push(curr);
    } else {
      groups.push(chunk);
      chunk = [curr];
    }
  }
  groups.push(chunk);
  return groups;
};

const PERMANENT_CONTRACT_REST_OF_MONTHS_INDEX = 2;

const getMonthlyRentGroupLabelSubtitle = (isRestOfMonths, first, last) => {
  if (isRestOfMonths) {
    return trans('landlordPaymentsTimeline.stay.restOfMonths');
  }
  if (first.paymentDate !== last.paymentDate) {
    return `${formatMonthlyPaymentDate(
      first.paymentDate
    )} - ${formatMonthlyPaymentDate(last.paymentDate)}`;
  }
  return formatMonthlyPaymentDate(first.paymentDate);
};

const DuringStay = ({
  costs = {},
  currencyCode,
  isCollapsed = true,
  contractDurationType = 'temporal',
  isSeasonalPricing = false
}) => {
  const isPermanentContract = contractDurationType === 'permanent';
  const duringStayCosts = costs?.costs ?? [];

  const totalValue = formatPrice(costs.total, currencyCode);
  const monthlyRentCosts = duringStayCosts.filter(
    c => c.category === COST_CATEGORY_MONTHLY_RENT
  );

  const allMonthsIncluded =
    monthlyRentCosts.length > 0 &&
    monthlyRentCosts.every(c => c.applyOn === APPLY_ON_INCLUDED);

  const headerValue = () => (
    <>
      {isSeasonalPricing && !hasVariableCosts
        ? `${trans('rentable_unit_price.label.from')} `
        : ''}
      {totalValue}{' '}
      {!allMonthsIncluded && (
        <span
          className={
            styles['payment-details-card__header-value-amount-per-month']
          }
        >
          / {trans('price_tag.label.per_month')}
        </span>
      )}
    </>
  );

  const hasRentCollection = monthlyRentCosts.some(
    c => c.payTo === PAYMENT_TO_SPOTAHOME
  );

  const billsCosts = duringStayCosts.filter(
    c => c.category === COST_CATEGORY_BILLS
  );

  const otherCosts = duringStayCosts.filter(
    c =>
      c.category !== COST_CATEGORY_MONTHLY_RENT &&
      c.category !== COST_CATEGORY_BILLS
  );

  const monthlyRentGroups = isPermanentContract
    ? monthlyRentCosts.map(c => [c])
    : groupConsecutiveMonthlyRentBySameValue(monthlyRentCosts);

  const hasVariableCosts =
    monthlyRentGroups.length > 1 ||
    otherCosts.some(c => isEstimatedCost(c.applyOn));

  const includedBills = billsCosts.filter(
    c => c.applyOn === APPLY_ON_INCLUDED && c.value === null
  );
  const nonIncludedBills = billsCosts.filter(
    c => c.applyOn !== APPLY_ON_INCLUDED || c.value !== null
  );

  const footer = hasRentCollection
    ? [
        {
          text: trans('payment.details.during_stay.rent_paid_to_spotahome'),
          tooltipText: trans(
            'payment.details.during_stay.rent_paid_to_spotahome.tooltip'
          )
        },
        {
          text: trans(
            'payment.details.during_stay.other_costs_paid_to_landlord'
          )
        }
      ]
    : undefined;

  const renderMonthlyRentCosts = () =>
    monthlyRentGroups.map(group => {
      const first = group[0];
      const last = group[group.length - 1];
      const startIndex = monthlyRentCosts.indexOf(first);
      const isRestOfMonths =
        isPermanentContract &&
        startIndex === PERMANENT_CONTRACT_REST_OF_MONTHS_INDEX;

      const labelSubtitle = getMonthlyRentGroupLabelSubtitle(
        isRestOfMonths,
        first,
        last
      );

      return (
        <DetailRow
          key={group.map(c => `${c.type}-${c.paymentDate}`).join('|')}
          compareValueForStrike={
            typeof first.value === 'number' ? first.value : null
          }
          currencyCode={currencyCode}
          included={first.applyOn === APPLY_ON_INCLUDED && startIndex === 0}
          includedCopy={trans(
            'spotahomeBookingPaymentInfo.downPaymentFirstMonthStrategy.firstMonthCovered'
          )}
          label={trans('pricing.details.rent.label')}
          labelSubtitle={labelSubtitle}
          tooltipText={getTooltipText(first, startIndex)}
          value={first.value}
          valueWithoutOffer={first.valueWithoutOffer}
          dataTest={
            isRestOfMonths
              ? 'landlordPaymentsTimeline.stay.monthRent.restOfMonths'
              : `landlordPaymentsTimeline.stay.monthRent.${startIndex}`
          }
        />
      );
    });

  const renderBillCosts = () =>
    billsCosts.length > 0 ? (
      <>
        {includedBills.length > 0 ? (
          <DetailRow
            key="bills-included-group"
            currencyCode={currencyCode}
            included
            label={includedBills
              .map(bill => formatCostTitle(bill.type))
              .join(', ')}
            valueSubtitle={
              includedBills
                .map(bill => getBillValueSubtitle(bill, currencyCode))
                .filter(Boolean)
                .join(', ') || null
            }
          />
        ) : null}
        {nonIncludedBills.map(cost => (
          <DetailRow
            key={cost.type}
            compareValueForStrike={
              typeof cost.value === 'number' ? cost.value : null
            }
            currencyCode={currencyCode}
            included={cost.applyOn === APPLY_ON_INCLUDED}
            label={formatCostTitle(cost.type)}
            value={getBillValue(cost)}
            valueSubtitle={getBillValueSubtitle(cost, currencyCode)}
            valueWithoutOffer={cost.valueWithoutOffer}
          />
        ))}
      </>
    ) : null;

  const renderDetailRowForOtherCost = cost => {
    const unit = getUnit(cost.applyOn);
    const estimatedSubtitle = isEstimatedCost(cost.applyOn)
      ? trans('payment.timeline.estimated-cost')
      : null;
    const formattedBase =
      typeof cost.value === 'number'
        ? formatPrice(cost.value, currencyCode)
        : cost.value;
    const displayValue =
      unit && typeof cost.value === 'number'
        ? trans(`price.per.${unit}`, { price: formattedBase })
        : formattedBase;

    return (
      <DetailRow
        key={`${cost.type}-${cost.paymentDate}`}
        currencyCode={currencyCode}
        included={cost.applyOn === APPLY_ON_INCLUDED}
        label={cost.name ?? trans(`payment.timeline.cost.label.${cost.type}`)}
        tooltipText={cost.description || null}
        value={displayValue}
        valueSubtitle={estimatedSubtitle}
        valueWithoutOffer={cost.valueWithoutOffer}
      />
    );
  };

  return (
    <PaymentDetailsCard
      dataTest="spotahomeBookingPaymentInfo.monthly"
      footer={footer}
      headerMeta={
        hasVariableCosts
          ? {
              text: trans('payment.details.during_stay.calculated_average'),
              tooltipText: trans(
                'payment.details.during_stay.calculated_average.tooltip'
              )
            }
          : undefined
      }
      headerValue={headerValue}
      isCollapsed={isCollapsed}
      title={trans('payment.timeline.section.during_stay.title')}
    >
      <>
        {renderMonthlyRentCosts()}
        {renderBillCosts()}

        {otherCosts.length > 0
          ? otherCosts.map(cost => renderDetailRowForOtherCost(cost))
          : null}
      </>
    </PaymentDetailsCard>
  );
};

DuringStay.propTypes = {
  costs: ListingCostsShape,
  currencyCode: PropTypes.string.isRequired,
  isCollapsed: PropTypes.bool,
  contractDurationType: PropTypes.string,
  isSeasonalPricing: PropTypes.bool
};

export default DuringStay;
